/** Restrictive sanitizer for Unstructured table HTML fragments (client-safe subset). */

const ALLOWED_TAGS = new Set([
  "TABLE",
  "THEAD",
  "TBODY",
  "TFOOT",
  "TR",
  "TH",
  "TD",
  "CAPTION",
  "COLGROUP",
  "COL",
]);

const ALLOWED_ATTRS = new Set(["colspan", "rowspan", "scope", "align"]);

function stripForbiddenTrees(root: Element): void {
  const remove: Element[] = [];
  const all = root.getElementsByTagName("*");
  for (let i = 0; i < all.length; i++) {
    const el = all[i];
    const tag = el.tagName.toUpperCase();
    if (tag === "SCRIPT" || tag === "STYLE" || tag === "IFRAME" || tag === "OBJECT" || tag === "EMBED") {
      remove.push(el);
    }
  }
  remove.forEach((el) => el.remove());
}

function cloneAllowedTableSubtree(node: Node): Node | null {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.cloneNode(false);
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const el = node as Element;
  const tag = el.tagName.toUpperCase();

  if (!ALLOWED_TAGS.has(tag)) {
    const frag = document.createDocumentFragment();
    el.childNodes.forEach((ch) => {
      const n = cloneAllowedTableSubtree(ch);
      if (n) frag.appendChild(n);
    });
    if (!frag.childNodes.length) return null;
    if (frag.childNodes.length === 1) return frag.firstChild;
    return frag;
  }

  const out = document.createElement(tag.toLowerCase());
  for (const attr of Array.from(el.attributes)) {
    const name = attr.name.toLowerCase();
    if (name.startsWith("on")) continue;
    const v = attr.value.trim().toLowerCase();
    if (v.startsWith("javascript:") || v.startsWith("data:text/html")) continue;
    if (ALLOWED_ATTRS.has(name)) {
      out.setAttribute(name, attr.value);
    }
  }
  el.childNodes.forEach((ch) => {
    const n = cloneAllowedTableSubtree(ch);
    if (n) out.appendChild(n);
  });
  return out;
}

export function sanitizeTableHtmlFragment(html: string): string {
  const raw = html.trim();
  if (!raw) return "";

  if (typeof document === "undefined" || typeof DOMParser === "undefined") {
    return "";
  }

  const doc = new DOMParser().parseFromString(raw, "text/html");
  stripForbiddenTrees(doc.body);

  const parts: string[] = [];
  doc.body.childNodes.forEach((node) => {
    const n = cloneAllowedTableSubtree(node);
    if (!n) return;
    if (n.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
      const frag = n as DocumentFragment;
      frag.childNodes.forEach((ch) => {
        if (ch.nodeType === Node.ELEMENT_NODE) {
          parts.push((ch as Element).outerHTML);
        }
      });
      return;
    }
    if (n.nodeType === Node.ELEMENT_NODE) {
      parts.push((n as Element).outerHTML);
    }
  });

  return parts.join("").trim();
}
